import React from "react";
import { Box, Flex, IconButton, Text, Grid, GridItem } from "@chakra-ui/react";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameDay,
  addDays,
  format,
  isSameMonth,
  parse,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { startOfDay } from "date-fns";
import { theme } from "src/styles/theme";

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface CustomCalendarProps {
  selectedRange: DateRange;
  setSelectedRange: React.Dispatch<React.SetStateAction<DateRange>>;
  dataAgendamento: string[];
  dataTarefas: string[];
}

const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedRange,
  setSelectedRange,
  dataAgendamento,
  dataTarefas,
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [hoverDate, setHoverDate] = React.useState<Date | null>(null);

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const handleDateClick = (day: Date) => {
    if (!selectedRange.from || (selectedRange.from && selectedRange.to)) {
      setSelectedRange({ from: day, to: undefined });
    } else if (selectedRange.from && !selectedRange.to) {
      if (day < selectedRange.from) {
        setSelectedRange({ from: day, to: selectedRange.from });
      } else {
        setSelectedRange({ from: selectedRange.from, to: day });
      }
    }
  };

  const hasAgendamento = (date: Date): boolean => {
    return dataAgendamento.some((dataStr) => {
      const parsed = parse(dataStr, "dd/MM/yyyy", new Date());
      return !isNaN(parsed.getTime()) && isSameDay(startOfDay(parsed), date);
    });
  };
  const hasTarefas = (date: Date): boolean => {
    return dataTarefas.some((dataStr) => {
      const parsed = parse(dataStr, "dd/MM/yyyy", new Date());
      return !isNaN(parsed.getTime()) && isSameDay(startOfDay(parsed), date);
    });
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const currentDay = startOfDay(day);

        const isStart =
          selectedRange.from && isSameDay(currentDay, selectedRange.from);
        const isEnd =
          selectedRange.to && isSameDay(currentDay, selectedRange.to);
        const isInRange =
          selectedRange.from &&
          selectedRange.to &&
          currentDay > selectedRange.from &&
          currentDay < selectedRange.to;

        const isSelected = isStart || isEnd;

        const bg = isSelected
          ? "#153D86"
          : isInRange
            ? "#EEF2FA"
            : "transparent";

        const isCurrentMonth = isSameMonth(currentDay, currentMonth);
        const color = isSelected
          ? "white"
          : isCurrentMonth
            ? theme.text
            : theme.disable;

        const colorWeight = isSelected
          ? "medium"
          : isInRange
            ? "medium"
            : "normal";

        days.push(
          <GridItem
            key={currentDay.getTime()}
            display={"flex"}
            flexDirection="column"
            alignItems={"center"}
            justifyContent={"center"}
            width="36px"
            height="36px"
            borderRadius={"full"}
            bg={bg}
            color={color}
            fontSize="sm"
            fontWeight={colorWeight}
            cursor="pointer"
            onClick={() => handleDateClick(currentDay)}
            onMouseEnter={() => setHoverDate(currentDay)}
            onMouseLeave={() => setHoverDate(null)}
            _hover={{
              bg: "#b3cff8",
            }}
          >
            <Text>{format(currentDay, "d")}</Text>
            <Box display={"flex"} gap={"1px"} w={"fit-content"}>
              {hasAgendamento(currentDay) && (
                <Box
                  mt="1px"
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={"#E96B73"}
                />
              )}
              {hasTarefas(currentDay) && (
                <Box
                  mt="1px"
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={"#FFAA00"}
                />
              )}
            </Box>
          </GridItem>
        );

        day = addDays(day, 1);
      }

      rows.push(
        <Grid
          templateColumns="repeat(7, 1fr)"
          textAlign="center"
          px={2}
          gap={2}
          pt={"1px"}
          key={day.getTime()}
        >
          {days}
        </Grid>
      );

      days = [];
    }

    return <>{rows}</>;
  };

  return (
    <Box overflow="hidden" fontSize="sm">
      <Flex justify="space-between" align="center" px={1} py={2}>
        <Text fontSize={"19px"} fontWeight="bold" pl={2}>
          {format(currentMonth, "MMMM yyyy", { locale: ptBR }).replace(
            /^./,
            (c) => c.toUpperCase()
          )}
        </Text>

        <Box display={"flex"} w={"fit-content"} gap={"4px"}>
          <IconButton
            aria-label="Previous Month"
            icon={<BsChevronLeft fontSize={"16px"} color={theme.primary} />}
            size="sm"
            variant="unstyled"
            onClick={handlePrevMonth}
            w={"fit-content"}
          />

          <IconButton
            aria-label="Next Month"
            icon={<BsChevronRight fontSize={"16px"} color={theme.primary} />}
            size="sm"
            variant="unstyled"
            onClick={handleNextMonth}
            w={"fit-content"}
          />
        </Box>
      </Flex>

      <Grid templateColumns="repeat(7, 1fr)" textAlign="center" px={2} mb={1}>
        {DAYS.map((day) => (
          <Box key={day} fontSize="xs" color="gray.500" py={1}>
            {day}
          </Box>
        ))}
      </Grid>

      <Box>
        {/* <Box px={2} pb={2}> */}
        {renderCells()}
      </Box>
    </Box>
  );
};

export default CustomCalendar;
